import type { ILanguageResources, ITranslationModule } from '../../../../interfaces';
import { interpolate } from '../../../../global';

class Id {
  private constructor() {}

  public static readonly prefix = 'UI.scripts.dealer-franchisee.quotes.views.FranchiseeQuoteDetailView.';
  public static readonly pleaseSelectClient = `${Id.prefix}pleaseSelectClient`;
  public static readonly pleaseSelectContact = `${Id.prefix}pleaseSelectContact`;
  public static readonly pleaseSelectOrEnterValidAddress = `${Id.prefix}pleaseSelectOrEnterValidAddress`;
  public static readonly pleaseSelectPaymentProfileForAccuratePricing = `${Id.prefix}pleaseSelectPaymentProfileForAccuratePricing`;
  public static readonly addClient = `${Id.prefix}addClient`;
  public static readonly pleaseSelectClientFirst = `${Id.prefix}pleaseSelectClientFirst`;
  public static readonly newButton = `${Id.prefix}newButton`;
  public static readonly client = `${Id.prefix}client`;
  public static readonly contact = `${Id.prefix}contact`;
  public static readonly paymentProfile = `${Id.prefix}paymentProfile`;
  public static readonly validUntilTooltip = `${Id.prefix}validUntilTooltip`;
  public static readonly installationDateTooltip = `${Id.prefix}installationDateTooltip`;
  public static readonly validToDate = `${Id.prefix}validToDate`;
  public static readonly validityPeriod = `${Id.prefix}validityPeriod`;
  public static readonly franchiseeOrderNo = `${Id.prefix}franchiseeOrderNo`;
  public static readonly dealerOrderNo = `${Id.prefix}dealerOrderNo`;
  public static readonly deliveryAddress = `${Id.prefix}deliveryAddress`;
  public static readonly deliverFromManufacturerTo = `${Id.prefix}deliverFromManufacturerTo`;
  public static readonly selectedDeliveryAddressUpdated = `${Id.prefix}selectedDeliveryAddressUpdated`;
  public static readonly title = `${Id.prefix}title`;
  public static readonly quoteBudget = `${Id.prefix}quoteBudget`;
  public static readonly description = `${Id.prefix}description`;
  public static readonly type = `${Id.prefix}type`;
  public static readonly author = `${Id.prefix}author`;
  public static readonly created = `${Id.prefix}created`;
  public static readonly lastModified = `${Id.prefix}lastModified`;
  public static readonly proposedDelivery = `${Id.prefix}proposedDelivery`;
  public static readonly updatePricingModelConfirmation = `${Id.prefix}updatePricingModelConfirmation`;
  public static readonly pricingModelUpdatedDueToProfile = `${Id.prefix}pricingModelUpdatedDueToProfile`;
  public static readonly paymentProfileChanged = `${Id.prefix}paymentProfileChanged`;
  public static readonly pleaseReviewQuoteItemTab = `${Id.prefix}pleaseReviewQuoteItemTab`;
  public static readonly address = `${Id.prefix}address`;
  public static readonly termsConditionsChanged = `${Id.prefix}termsConditionsChanged`;
  public static readonly termsConditionsCleared = `${Id.prefix}termsConditionsCleared`;
}

export class LangFranchiseeQuoteDetailView {
  private constructor() {}

  public static pleaseSelectClient(): string {
    return interpolate(Id.pleaseSelectClient);
  }

  public static pleaseSelectContact(): string {
    return interpolate(Id.pleaseSelectContact);
  }

  public static pleaseSelectOrEnterValidAddress(): string {
    return interpolate(Id.pleaseSelectOrEnterValidAddress);
  }

  public static pleaseSelectPaymentProfileForAccuratePricing(): string {
    return interpolate(Id.pleaseSelectPaymentProfileForAccuratePricing);
  }

  public static addClient(): string {
    return interpolate(Id.addClient);
  }

  public static pleaseSelectClientFirst(): string {
    return interpolate(Id.pleaseSelectClientFirst);
  }

  public static newButton(): string {
    return interpolate(Id.newButton);
  }

  public static client(): string {
    return interpolate(Id.client);
  }

  public static contact(): string {
    return interpolate(Id.contact);
  }

  public static paymentProfile(): string {
    return interpolate(Id.paymentProfile);
  }

  public static validUntilTooltip(): string {
    return interpolate(Id.validUntilTooltip);
  }

  public static installationDateTooltip(): string {
    return interpolate(Id.installationDateTooltip);
  }

  public static validToDate(): string {
    return interpolate(Id.validToDate);
  }

  public static validityPeriod(): string {
    return interpolate(Id.validityPeriod);
  }

  public static franchiseeOrderNo(): string {
    return interpolate(Id.franchiseeOrderNo);
  }

  public static dealerOrderNo(params: { dealerName: string }): string {
    return interpolate(Id.dealerOrderNo, params);
  }

  public static deliveryAddress(): string {
    return interpolate(Id.deliveryAddress);
  }

  public static deliverFromManufacturerTo(): string {
    return interpolate(Id.deliverFromManufacturerTo);
  }

  public static selectedDeliveryAddressUpdated(): string {
    return interpolate(Id.selectedDeliveryAddressUpdated);
  }

  public static title(): string {
    return interpolate(Id.title);
  }

  public static quoteBudget(): string {
    return interpolate(Id.quoteBudget);
  }

  public static description(): string {
    return interpolate(Id.description);
  }

  public static type(): string {
    return interpolate(Id.type);
  }

  public static author(): string {
    return interpolate(Id.author);
  }

  public static created(): string {
    return interpolate(Id.created);
  }

  public static lastModified(): string {
    return interpolate(Id.lastModified);
  }

  public static proposedDelivery(): string {
    return interpolate(Id.proposedDelivery);
  }

  public static updatePricingModelConfirmation(params: {
    currentCalculation: string;
    currentValue: number;
    newCalculation: string;
    newValue: number;
  }): string {
    return interpolate(Id.updatePricingModelConfirmation, params);
  }

  public static pricingModelUpdatedDueToProfile(): string {
    return interpolate(Id.pricingModelUpdatedDueToProfile);
  }

  public static paymentProfileChanged(): string {
    return interpolate(Id.paymentProfileChanged);
  }

  public static pleaseReviewQuoteItemTab(): string {
    return interpolate(Id.pleaseReviewQuoteItemTab);
  }

  public static address(): string {
    return interpolate(Id.address);
  }

  public static termsConditionsChanged(): string {
    return interpolate(Id.termsConditionsChanged);
  }

  public static termsConditionsCleared(): string {
    return interpolate(Id.termsConditionsCleared);
  }
}

export class FranchiseeQuoteDetailViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.pleaseSelectClient, "Please select a {{glossary 'client'}}", 'Validation: select client');
    register.add(Id.pleaseSelectContact, "Please select a {{glossary 'contact'}}", 'Validation: select contact');
    register.add(
      Id.pleaseSelectOrEnterValidAddress,
      'Please select or enter a valid address',
      'Validation: select or enter address'
    );
    register.add(
      Id.pleaseSelectPaymentProfileForAccuratePricing,
      "Please select a {{glossary 'payment-profile'}} for accurate pricing",
      'Validation: select payment profile'
    );
    register.add(Id.addClient, "Add {{glossary 'client'}}", 'Button: add client');
    register.add(
      Id.pleaseSelectClientFirst,
      "Please select a {{glossary 'client'}} first.",
      'Validation: select client first'
    );
    register.add(Id.newButton, 'New', 'Button: new');
    register.add(Id.client, "{{glossary 'client'}}", 'Label: client');
    register.add(Id.contact, "{{glossary 'contact'}}", 'Label: contact');
    register.add(Id.paymentProfile, "{{glossary 'payment-profile'}}", 'Label: payment profile');
    register.add(
      Id.validUntilTooltip,
      "The system calculates the expiry date based on the default validity period (in ‘settings’) from the current date (e.g. today + 28 days), every day until the date you actually issue the {{glossary 'quote'}}.",
      'Tooltip: valid until'
    );
    register.add(
      Id.installationDateTooltip,
      'Ensure that you consider the supplier’s scheduling, ordering, fabrication & shipping lead times before committing to a proposed delivery date to the client. If in doubt, contact the supplier for confirmation.',
      'Tooltip: installation date'
    );
    register.add(Id.validToDate, "'Valid To' Date", 'Label: valid to date');
    register.add(Id.validityPeriod, 'Validity Period', 'Label: validity period');
    register.add(Id.franchiseeOrderNo, "{{glossary 'franchisee'}} Order No.", 'Label: franchisee order no');
    register.add(Id.dealerOrderNo, '{{dealerName}} Order No.', 'Label: dealer order no');
    register.add(Id.deliveryAddress, 'Delivery Address', 'Label: delivery address');
    register.add(
      Id.deliverFromManufacturerTo,
      'Deliver from the manufacturer to....',
      'Section title: deliver from manufacturer'
    );
    register.add(
      Id.selectedDeliveryAddressUpdated,
      "The currently selected delivery address has been updated. Please review the {{glossary 'shipping-note:plural'}} to ensure that they are still valid.",
      'Warning: delivery address updated'
    );
    register.add(Id.title, 'Title', 'Label: title');
    register.add(Id.quoteBudget, 'Quote Budget', 'Label: quote budget');
    register.add(Id.description, 'Description', 'Label: description');
    register.add(Id.type, 'Type', 'Label: type');
    register.add(Id.author, 'Author', 'Label: author');
    register.add(Id.created, 'Created', 'Label: created');
    register.add(Id.lastModified, 'Last Modified', 'Label: last modified');
    register.add(Id.proposedDelivery, 'Proposed Delivery', 'Label: proposed delivery');
    register.add(
      Id.updatePricingModelConfirmation,
      "Would you like to change the pricing model from '{{currentCalculation}} {{currentValue}}%' to '{{newCalculation}} {{newValue}}%' on the Quote Summary page? This will alter the Quote Price.",
      'Confirmation for updating pricing model and terms'
    );
    register.add(
      Id.pricingModelUpdatedDueToProfile,
      "Due to an update to the pricing model associated with the ‘{{glossary 'payment-profile'}}’, the quote has inherited the Pricing Metric.",
      'Message for pricing model update due to payment profile'
    );
    register.add(
      Id.paymentProfileChanged,
      "The ‘{{glossary 'payment-profile'}}’ change has updated the associated Pricing Metric.",
      'Message for payment profile change'
    );
    register.add(
      Id.pleaseReviewQuoteItemTab,
      "Please review details on the {{glossary 'quote-item:plural'}} tab.",
      'Prompt to review quote item tab'
    );
    register.add(Id.address, 'Address', 'Label: address');
    register.add(
      Id.termsConditionsChanged,
      "The {{glossary 'terms-conditions'}} have been changed.",
      'Message for terms and conditions changed'
    );
    register.add(
      Id.termsConditionsCleared,
      "The {{glossary 'terms-conditions'}} have been cleared as the selected {{glossary 'client'}} has none.",
      'Message for terms and conditions cleared'
    );
  }
}
